import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  AangifteBerekening,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from "@angular/forms";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  Observable,
  shareReplay,
  switchMap,
  take
} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-overzicht-inkomen-box3',
    templateUrl: './overzicht-inkomen-box3.component.html',
    styleUrls: ['./overzicht-inkomen-box3.component.scss'],
    imports: [SharedUiDetailsModule, SharedUiFormControlsModule, CommonModule, SharedUiAlertModule],
})
export class OverzichtInkomenBox3Component implements OnInit {
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private bokkzClient = inject(IbRekenService);
    public dataVisible$ = new BehaviorSubject<boolean>(false);
    public heeftOwr = false;
    public isPartner = false;
    public isPartnerView = false;

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public formGroup = new FormGroup({
        inkomen: new FormControl<number | null>(0),
        persoonsgebondenAftrek: new FormControl<number | null>(0),
        belastbaarInkomen: new FormControl<number | null>(0),
        opbouwInkomen: new FormGroup({
            voordeelUitSparenEnBeleggen: new FormControl<number | null>(0),
            grondslagNaVerdeling: new FormControl<number | null>(0),
            grondslagSparenEnBeleggen: new FormControl<number | null>(0),
            rendementsGrondslagNaVerdeling: new FormControl<number | null>(0),
            rendementsGrondslag: new FormControl<number | null>(0),
        }),
        forfaitairRendement: new FormControl<number | null>(0),
        werkelijkRendement: new FormControl<number | null>(0),
        toegepasteRendement: new FormControl<number | null>(0),
    });

    ngOnInit(): void {
        const berekening$ = this.incomeTaxEntity$.pipe(
            take(1),
            filter((data): data is IncomeTaxEntity => !!data.aangifte),
            switchMap((data) => {
                this.heeftOwr = data.aangifte?.box3?.berekenWerkelijkRendement ?? false;
                this.isPartner = data.aangifte?.gezamenlijkeAangifte ?? false;
                return this.bokkzClient.getIncomeTaxCalculation(data.aangifte!);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
            takeUntilDestroyed(this.destroyRef),
        );

        combineLatest([berekening$, this.dataService.partnerAangifte$.pipe(distinctUntilChanged())])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([berekening, isPartner]) => {
                this.dataVisible$.next(false);
                this.resetAndPatchForms(isPartner ? (berekening.partnerBerekening ?? berekening) : berekening);
                if (this.dataVisible$.value) {
                    this.formGroup.patchValue(
                        {
                            forfaitairRendement:
                                (berekening.box3?.opbouwInkomen?.forfaitairVoordeelUitSparenEnBeleggen ?? 0) +
                                (berekening.partnerBerekening?.box3?.opbouwInkomen?.forfaitairVoordeelUitSparenEnBeleggen ?? 0),
                        },
                        { emitEvent: false },
                    );
                    this.formGroup.patchValue(
                        {
                            werkelijkRendement:
                                (berekening.box3?.opbouwInkomen?.werkelijkVoordeelUitSparenEnBeleggen ?? 0) +
                                (berekening.partnerBerekening?.box3?.opbouwInkomen?.werkelijkVoordeelUitSparenEnBeleggen ?? 0),
                        },
                        { emitEvent: false },
                    );
                    this.formGroup.patchValue(
                        { toegepasteRendement: (berekening.box3?.inkomen ?? 0) + (berekening.partnerBerekening?.box3?.inkomen ?? 0) },
                        { emitEvent: false },
                    );
                }
                this.isPartnerView = isPartner;
            });
    }

    private resetAndPatchForms(details: AangifteBerekening): void {
        this.formGroup.reset({}, { emitEvent: false });
        this.formGroup.patchValue(details.box3 ?? {}, { emitEvent: false });
        this.formGroup.disable({ emitEvent: false });
        this.dataVisible$.next(true);
    }
}
